import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Check, Circle, Loader2, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const NAMES = { proposal: "Proposal", research: "Research", draft: "Draft", presentation: "Presentation" };
const STATUS_ORDER = ["not_started", "in_progress", "done"];

export default function EpqMilestones({ compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/epq/milestones");
        setItems(data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = async (id, patch) => {
    try {
      const { data } = await api.patch(`/epq/milestones/${id}`, patch);
      setItems((arr) => arr.map((m) => (m.id === id ? data : m)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cycleStatus = (m) => {
    const idx = STATUS_ORDER.indexOf(m.status);
    const next = STATUS_ORDER[(idx + 1) % STATUS_ORDER.length];
    update(m.id, { status: next });
  };

  if (loading) return <div className="text-sm text-muted flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> loading milestones…</div>;

  const doneCount = items.filter((m) => m.status === "done").length;

  if (compact) {
    // Dashboard: horizontal 4-node bar
    return (
      <div className="mt-3" data-testid="epq-milestones-compact">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-wider text-muted">milestones</span>
          <span className="font-hand text-base" style={{ color: "var(--accent)" }}>{doneCount} / 4</span>
        </div>
        <div className="flex items-center gap-2">
          {items.map((m, i) => (
            <React.Fragment key={m.id}>
              <div
                className="flex-1 flex items-center gap-2 px-2 py-2 rounded-xl text-xs font-medium"
                style={{
                  background: m.status === "done"
                    ? "color-mix(in oklab, var(--primary) 25%, var(--surface))"
                    : "var(--surface-2)",
                  border: "1px solid var(--border)",
                  color: m.status === "done" ? "var(--primary)" : "var(--text-muted)",
                }}
                data-testid={`epq-node-${m.milestone_name}`}
                title={NAMES[m.milestone_name]}
              >
                {m.status === "done" ? <Check size={12} strokeWidth={2} /> : m.status === "in_progress" ? <Loader2 size={12} className="animate-spin" strokeWidth={1.75} /> : <Circle size={12} strokeWidth={1.5} />}
                <span className="truncate hidden sm:inline">{NAMES[m.milestone_name]}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="epq-milestones-full">
      {items.map((m) => (
        <div key={m.id} className="card-soft-2 p-4" data-testid={`epq-milestone-${m.milestone_name}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => cycleStatus(m)}
                className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-105"
                style={{
                  background: m.status === "done"
                    ? "var(--primary)"
                    : m.status === "in_progress"
                      ? "color-mix(in oklab, var(--primary) 30%, var(--surface))"
                      : "var(--surface-2)",
                  color: m.status === "done" ? "#fff" : "var(--text-muted)",
                  border: "1px solid var(--border)",
                }}
                data-testid={`epq-status-toggle-${m.milestone_name}`}
                aria-label={`Cycle status of ${NAMES[m.milestone_name]}`}
              >
                {m.status === "done" ? <Check size={16} strokeWidth={2} />
                  : m.status === "in_progress" ? <Loader2 size={16} className="animate-spin" strokeWidth={1.75} />
                  : <Circle size={16} strokeWidth={1.5} />}
              </button>
              <div>
                <div className="font-display text-xl leading-tight" style={{ color: "var(--text)" }}>{NAMES[m.milestone_name]}</div>
                <div className="text-xs text-muted">{m.status.replace("_", " ")}</div>
              </div>
            </div>
            <button
              className="btn-ghost text-xs"
              onClick={() => setExpandedId(expandedId === m.id ? null : m.id)}
              data-testid={`epq-expand-${m.milestone_name}`}
            >
              {expandedId === m.id ? "Close" : "Details"}
            </button>
          </div>

          {expandedId === m.id && (
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted mb-1 flex items-center gap-1">
                  <CalendarDays size={12} strokeWidth={1.5} /> Target date
                </label>
                <input
                  type="date"
                  className="input-soft"
                  value={m.target_date || ""}
                  onChange={(e) => update(m.id, { target_date: e.target.value })}
                  data-testid={`epq-target-${m.milestone_name}`}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted mb-1 block">Notes</label>
                <textarea
                  className="input-soft"
                  placeholder="Anything to remember about this milestone…"
                  value={m.notes || ""}
                  onChange={(e) => update(m.id, { notes: e.target.value })}
                  data-testid={`epq-notes-${m.milestone_name}`}
                />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
